"use client";

import { motion } from "framer-motion";
import { Ship, Shield, Users, Wrench, BarChart3 } from "lucide-react";
import SectionHero from "../components/SectionHero";
import { useEffect } from "react";

const OurFleet = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);
  const fleetTypes = [
    { type: "Bulk Carriers", description: "General cargo vessels" },
    { type: "Tankers", description: "Product and chemical tankers" },
    { type: "Offshore Support", description: "Utility and support vessels" },
    { type: "Specialised Craft", description: "Port and coastal vessels" },
  ];

  const managementPrinciples = [
    {
      icon: Wrench,
      title: "Structured Maintenance",
      description:
        "Comprehensive maintenance and inspection routines to ensure optimal vessel performance",
    },
    {
      icon: Shield,
      title: "Regulatory Compliance",
      description:
        "Full compliance with international and flag state regulations",
    },
    {
      icon: Users,
      title: "Crew Excellence",
      description: "Focus on crew competence, training, and safety culture",
    },
    {
      icon: BarChart3,
      title: "Transparent Reporting",
      description:
        "Clear reporting to owners and charterers on vessel performance",
    },
  ];

  return (
    <div className="pt-20">
      <SectionHero
        title="Our Fleet"
        subtitle="A Diverse Portfolio, Managed with a Common Standard"
        description="Our fleet reflects the diversity of modern shipping. What remains constant is our commitment to safety, compliance, and reliable operation."
      />

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center md:mb-16"
          >
            <h2 className="mb-4 text-2xl font-bold text-slate-900 sm:text-3xl md:text-4xl">
              Fleet Composition
            </h2>
            <p className="mx-auto max-w-3xl text-slate-600">
              Our managed fleet includes various vessel types operating across
              different segments and trades.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {fleetTypes.map((type, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="rounded-xl bg-slate-50 p-6 text-center"
              >
                <Ship className="mb-4 h-8 w-8 text-teal-600 mx-auto" />
                <h3 className="mb-2 font-bold text-slate-900">{type.type}</h3>
                <p className="text-sm text-slate-600">{type.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center md:mb-16"
          >
            <h2 className="mb-4 text-2xl font-bold text-slate-900 sm:text-3xl md:text-4xl">
              Management Excellence
            </h2>
            <p className="mx-auto max-w-3xl text-slate-600">
              Every vessel, regardless of size or trade, receives the same level
              of attention and care.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {managementPrinciples.map((principle, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="rounded-xl bg-white p-6 shadow-lg"
              >
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-teal-100">
                  <principle.icon className="h-6 w-6 text-teal-600" />
                </div>
                <h3 className="mb-2 font-bold text-slate-900">
                  {principle.title}
                </h3>
                <p className="text-sm text-slate-600">
                  {principle.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-br from-slate-900 to-slate-800 py-16 text-white md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="mb-4 text-2xl font-bold sm:text-3xl md:text-4xl">
              Fleet Performance Standards
            </h2>
            <p className="mx-auto mb-8 max-w-3xl text-slate-300">
              All vessels under our management follow consistent standards for
              maintenance, compliance, crew welfare, and transparent reporting.
              This consistency ensures reliability for owners, charterers, and
              stakeholders.
            </p>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <div className="rounded-lg bg-white/10 p-6 backdrop-blur">
                <div className="mb-2 text-3xl font-bold text-teal-400">99%</div>
                <p className="text-slate-200">On-Time Performance</p>
              </div>
              <div className="rounded-lg bg-white/10 p-6 backdrop-blur">
                <div className="mb-2 text-3xl font-bold text-teal-400">
                  100%
                </div>
                <p className="text-slate-200">Regulatory Compliance</p>
              </div>
              <div className="rounded-lg bg-white/10 p-6 backdrop-blur">
                <div className="mb-2 text-3xl font-bold text-teal-400">50+</div>
                <p className="text-slate-200">Vessels Managed</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default OurFleet;
